import React, { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Switch } from '../ui/switch';
import { Slider } from '../ui/slider';
import { Badge } from '../ui/badge';
import { Plus, Trash2, MoveUp, MoveDown, Save, Volume2, Music2 } from 'lucide-react';
import adminAPI from './AdminUtils';
import { useAdmin } from './AdminHooks';

const ModernMusicPlayerTab = () => {
  const { user } = useAdmin();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [options, setOptions] = useState({ autoplay: false, defaultVolume: 20, startMuted: true, shuffle: false });
  const [newItem, setNewItem] = useState({ url: '', title: '', visible: true });

  const fetchAll = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.get('/music/admin');
      setItems(res.data.items || []);
      const o = res.data.options || {};
      setOptions({
        autoplay: !!o.autoplay,
        defaultVolume: typeof o.defaultVolume === 'number' ? o.defaultVolume : 20,
        startMuted: !!o.startMuted,
        shuffle: !!o.shuffle,
      });
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const addItem = async (e) => {
    e.preventDefault();
    if (!newItem.url) return;
    setSaving(true);
    try {
      const res = await adminAPI.post('/music/items', { url: newItem.url, title: newItem.title || null, visible: !!newItem.visible, order: items.length });
      setItems(prev => [...prev, res.data]);
      setNewItem({ url: '', title: '', visible: true });
    } catch {
      alert('Erreur lors de l’ajout');
    } finally {
      setSaving(false);
    }
  };

  const updateItem = async (id, fields) => {
    setSaving(true);
    try {
      const res = await adminAPI.put(`/music/items/${id}`, fields);
      setItems(prev => prev.map(it => it.id === id ? res.data : it));
    } catch {
      alert('Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  const removeItem = async (id) => {
    if (!window.confirm('Supprimer ce morceau ?')) return;
    setSaving(true);
    try {
      await adminAPI.delete(`/music/items/${id}`);
      setItems(prev => prev.filter(it => it.id !== id).map((it, i) => ({ ...it, order: i })));
      const reordered = items.filter(it => it.id !== id).map((it, i) => ({ id: it.id, order: i }));
      if (reordered.length) await adminAPI.post('/music/reorder', reordered);
    } catch {
      alert('Erreur lors de la suppression');
    } finally {
      setSaving(false);
    }
  };

  const move = async (index, direction) => {
    const ni = index + direction;
    if (ni < 0 || ni >= items.length) return;
    const arr = [...items];
    const tmp = arr[index];
    arr[index] = arr[ni];
    arr[ni] = tmp;
    const reordered = arr.map((it, i) => ({ id: it.id, order: i }));
    setItems(arr.map((it, i) => ({ ...it, order: i })));
    try {
      await adminAPI.post('/music/reorder', reordered);
    } catch {
      // ignore
    }
  };

  const saveOptions = async () => {
    setSaving(true);
    try {
      const payload = {
        autoplay: !!options.autoplay,
        defaultVolume: Math.max(0, Math.min(100, parseInt(options.defaultVolume) || 0)),
        startMuted: !!options.startMuted,
        shuffle: !!options.shuffle,
      };
      await adminAPI.put('/music/options', payload);
      alert('Options sauvegardées');
    } catch {
      alert('Erreur lors de la sauvegarde des options');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card className="bg-slate-900 border-slate-700">
        <CardHeader>
          <CardTitle className="flex gap-2 items-center"><Music2 size={18} /> Lecteur musical</CardTitle>
          <CardDescription>Gérez la playlist YouTube (audio uniquement) et les options de lecture.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <form onSubmit={addItem} className="flex flex-col gap-3 md:flex-row">
            <Input
              placeholder="URL YouTube"
              value={newItem.url}
              onChange={(e) => setNewItem(prev => ({ ...prev, url: e.target.value }))}
              className="flex-1"
            />
            <Input
              placeholder="Titre (facultatif)"
              value={newItem.title}
              onChange={(e) => setNewItem(prev => ({ ...prev, title: e.target.value }))}
              className="flex-1"
            />
            <div className="flex gap-2 items-center">
              <span className="text-sm text-slate-300">Visible</span>
              <Switch checked={newItem.visible} onCheckedChange={(v) => setNewItem(prev => ({ ...prev, visible: v }))} />
            </div>
            <Button type="submit" disabled={saving}><Plus size={16} className="mr-1" />Ajouter</Button>
          </form>
          <div className="space-y-2">
            {loading ? (
              <div className="text-sm text-slate-400">Chargement…</div>
            ) : items.length === 0 ? (
              <div className="text-sm text-slate-400">Aucun morceau</div>
            ) : (
              items.map((it, idx) => (
                <div key={it.id} className="flex gap-2 items-center p-2 rounded border bg-slate-800/50 border-slate-700">
                  <Badge variant="outline" className="border-slate-600 text-slate-300">#{idx + 1}</Badge>
                  <Input value={it.title || ''} onChange={(e) => setItems(prev => prev.map(x => x.id === it.id ? { ...x, title: e.target.value } : x))} className="flex-1" placeholder="Titre" />
                  <Input value={it.url} onChange={(e) => setItems(prev => prev.map(x => x.id === it.id ? { ...x, url: e.target.value } : x))} className="flex-1" placeholder="URL YouTube" />
                  <div className="flex gap-2 items-center">
                    <span className="text-xs text-slate-300">Visible</span>
                    <Switch checked={!!it.visible} onCheckedChange={(v) => setItems(prev => prev.map(x => x.id === it.id ? { ...x, visible: v } : x))} />
                  </div>
                  <Button variant="ghost" onClick={() => move(idx, -1)} title="Monter"><MoveUp size={16} /></Button>
                  <Button variant="ghost" onClick={() => move(idx, +1)} title="Descendre"><MoveDown size={16} /></Button>
                  <Button variant="secondary" onClick={() => updateItem(it.id, { title: it.title || null, url: it.url, visible: !!it.visible })}><Save size={16} /></Button>
                  <Button variant="ghost" onClick={() => removeItem(it.id)} title="Supprimer" className="text-red-400 hover:text-red-300 hover:bg-red-500/10"><Trash2 size={16} /></Button>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>
      <Card className="bg-slate-900 border-slate-700">
        <CardHeader>
          <CardTitle className="flex gap-2 items-center"><Volume2 size={18} /> Options</CardTitle>
          <CardDescription>Réglage global du lecteur sur le site.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex gap-2 items-center">
              <span className="text-sm text-slate-300">Lecture auto</span>
              <Switch checked={options.autoplay} onCheckedChange={(v) => setOptions(prev => ({ ...prev, autoplay: v }))} />
            </div>
            <div className="flex gap-2 items-center">
              <span className="text-sm text-slate-300">Démarrer muet</span>
              <Switch checked={options.startMuted} onCheckedChange={(v) => setOptions(prev => ({ ...prev, startMuted: v }))} />
            </div>
            <div className="flex gap-2 items-center">
              <span className="text-sm text-slate-300">Aléatoire</span>
              <Switch checked={options.shuffle} onCheckedChange={(v) => setOptions(prev => ({ ...prev, shuffle: v }))} />
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between text-xs text-slate-400">
              <span>Volume par défaut</span>
              <span>{options.defaultVolume}%</span>
            </div>
            <Slider value={[options.defaultVolume]} min={0} max={100} step={1} onValueChange={(v) => setOptions(prev => ({ ...prev, defaultVolume: v[0] }))} />
          </div>
          <Button onClick={saveOptions} disabled={saving}><Save size={16} className="mr-1" />Sauvegarder</Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default ModernMusicPlayerTab;
